/**
 * Fair Home V2 — H10 persona wiring
 * Maps auth + `/v1/personalization/status` → Selected / AI copy states
 * Score comes from the server — never recompute. The Capsules section reads its
 * own `personalization` block from `GET /capsules` (fair-home-v2-capsules.data).
 *
 * Note: auth-mismatch (token + anonymous-shaped status) is byte-identical to a
 * real newcomer — see fair-search-v2-auth-mismatch.data.ts. Home prefers the
 * newcomer pitch for that shape; Search hides the quiz CTA instead.
 */
import type { PersonalizationStatusResponse } from '@/features/search/api/searchV1'
import type { FairHomeV2AiPersonaId } from './fair-home-v2-ai.data'
import type { FairHomeV2SelectedPersonaId } from './fair-home-v2-selected.data'

export type FairHomeV2SectionLinkAction = 'auth' | 'taste' | 'profile'

export interface FairHomeV2PersonaState {
  selected: FairHomeV2SelectedPersonaId
  ai: FairHomeV2AiPersonaId
  /** Server personalization score 0..100 — AI completeness */
  score: number
  sectionLinkAction: FairHomeV2SectionLinkAction | null
}

const GUEST: FairHomeV2PersonaState = {
  selected: 'guest',
  ai: 'guest',
  score: 0,
  sectionLinkAction: 'auth',
}

/**
 * BE-190 / audit #4: the AI block's copy follows the SAME level its badge shows
 * (`status.level.current`), not the score's `next_action` — otherwise a novice read
 * "Newcomer" beside the Advanced text. `null` → no level on the answer (older API).
 */
const AI_PERSONA_BY_LEVEL: Readonly<Record<string, FairHomeV2AiPersonaId>> = {
  novice: 'newcomer',
  explorer: 'explorer',
  advanced: 'advanced',
  expert: 'profile-complete',
}

export function fairHomeV2AiPersonaFromLevel(
  current: string | null | undefined,
): FairHomeV2AiPersonaId | null {
  return (current && AI_PERSONA_BY_LEVEL[current]) || null
}

/**
 * Resolve Home v2 personas from auth + personalization status.
 *
 * - Guest: no token
 * - next_action null → profile complete
 * - next_action style_quiz → newcomer
 * - measurements → explorer / registered-partial
 * - interactions | search_history → advanced / registered-partial
 * - the AI persona: from `status.level.current` whenever the answer has a level (BE-190)
 */
export function resolveFairHomeV2PersonaState(input: {
  isAuthenticated: boolean
  status: PersonalizationStatusResponse | null | undefined
}): FairHomeV2PersonaState {
  if (!input.isAuthenticated) return GUEST

  const status = input.status
  if (!status) {
    return {
      selected: 'registered-partial',
      ai: 'registered-partial',
      score: 0,
      sectionLinkAction: 'profile',
    }
  }

  const state = resolveFromNextAction(status)
  const byLevel = fairHomeV2AiPersonaFromLevel(status.level?.current)
  return byLevel ? { ...state, ai: byLevel } : state
}

/** The pre-BE-190 rules: the Selected persona and, without a level, the AI persona. */
function resolveFromNextAction(status: PersonalizationStatusResponse): FairHomeV2PersonaState {
  const score = status.score
  const next = status.next_action

  if (next == null) {
    return {
      selected: 'profile-complete',
      ai: 'profile-complete',
      score,
      sectionLinkAction: null,
    }
  }

  if (next === 'style_quiz') {
    return {
      selected: 'newcomer',
      ai: 'newcomer',
      score,
      sectionLinkAction: 'taste',
    }
  }

  if (next === 'measurements') {
    return {
      selected: 'registered-partial',
      ai: 'explorer',
      score,
      sectionLinkAction: 'profile',
    }
  }

  // interactions | search_history — deeper partial
  return {
    selected: 'registered-partial',
    ai: 'advanced',
    score,
    sectionLinkAction: 'profile',
  }
}
